import type { ThemeMode } from '@/hooks/useLightMode'

import { Button, Flex, Spinner, Stack, Text } from '@hivewatch/dark'

import {
  settingsColorToken,
  settingsStateBodyTextColorToken,
  settingsTableStateTextColorToken,
} from '../utils/settingsColorToken'

type SettingsStateMessageVariant = 'default' | 'table'

interface SettingsStateMessageIProps {
  actionLabel?: string
  body?: string
  colorScheme: ThemeMode
  gap?: string
  height?: string
  isLoading?: boolean
  onAction?: () => void
  title?: string
  variant?: SettingsStateMessageVariant
}

export const SettingsStateMessage = ({
  actionLabel,
  body,
  colorScheme,
  gap = '8px',
  height,
  isLoading = false,
  onAction,
  title,
  variant = 'default',
}: SettingsStateMessageIProps) => {
  const isTable = variant === 'table'
  const tableTextColor = settingsTableStateTextColorToken(colorScheme)
  const titleColor = isTable
    ? tableTextColor
    : settingsColorToken(colorScheme, 'text.primary')
  const bodyColor = isTable
    ? tableTextColor
    : settingsStateBodyTextColorToken(colorScheme)

  return (
    <Flex
      alignItems='center'
      direction='column'
      h={height}
      justifyContent='center'
      minH={height ? undefined : '180px'}
      px={isTable ? undefined : '24px'}
      textAlign={isTable ? undefined : 'center'}
    >
      {isLoading ? (
        <Spinner colorScheme={colorScheme} size='lg' />
      ) : (
        <Stack alignItems='center' gap={gap}>
          {title && (
            <Text
              as={isTable ? 'p' : 'h4'}
              color={titleColor}
              textStyle='body-bold'
            >
              {title}
            </Text>
          )}
          {body && (
            <Text
              color={bodyColor}
              maxW={isTable ? undefined : '480px'}
              textStyle='body'
            >
              {body}
            </Text>
          )}
          {actionLabel && onAction && (
            <Button
              colorScheme={colorScheme}
              onClick={onAction}
              variant='secondary'
            >
              {actionLabel}
            </Button>
          )}
        </Stack>
      )}
    </Flex>
  )
}
